import { getUser } from '../pages/Session';

/** Parse comma-separated practice owner ids (must match backend CUSTOM_PRACTICE_ACCOUNT_IDS). */
export function parseCustomPracticeAccountIds(raw = '') {
  return String(raw ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => !Number.isNaN(id) && id > 0);
}

/**
 * Progress Note custom fields (Objectives, Client Note, header) — practice owner id + child clinicians.
 * Staging: 14, 146 — Production: switch to 300, 98 when deploying live.
 */
export const CUSTOM_PRACTICE_ACCOUNT_IDS = [300,98];

export function getPowerAccountId(user) {
  if (!user) {
    return null;
  }
  if (user.power_account !== undefined && user.power_account !== null) {
    return Number(user.power_account);
  }
  const parentAccount = Number(user.parent_account);
  const userId = Number(user.id);
  if (!parentAccount || parentAccount === 0) {
    return userId;
  }
  return parentAccount;
}

export function isCustomPracticeAccount(user = getUser()) {
  const powerId = getPowerAccountId(user);
  if (powerId == null || CUSTOM_PRACTICE_ACCOUNT_IDS.length === 0) {
    return false;
  }
  return CUSTOM_PRACTICE_ACCOUNT_IDS.includes(powerId);
}

/** Custom practice UI + fields apply only to these document types */
export const CUSTOM_PRACTICE_DOCUMENT_TYPES = ['progress_note', 'assessment'];

export function isCustomPracticeDocumentType(documentType) {
  return CUSTOM_PRACTICE_DOCUMENT_TYPES.includes(documentType);
}

/** Objectives + Client Note accordions are Progress Note only */
export function supportsCustomPracticeObjectives(documentType) {
  return documentType === 'progress_note';
}

/** Client Note accordion is Progress Note only (not Assessment) */
export function supportsCustomPracticeClientNote(documentType) {
  return documentType === 'progress_note';
}

export function isCustomPracticeNotePage(documentType, user = getUser()) {
  return isCustomPracticeAccount(user) && isCustomPracticeDocumentType(documentType);
}

/** Default meta object — same keys used in API, DB, and UI */
export const CUSTOM_PRACTICE_META_DEFAULTS = {
  start_time: '',
  end_time: '',
  place_of_service: 'In-Home',
  method_of_contact: 'In-Person',
  ref_id: '',
  ins_id: '',
};

/** Ignore null/epoch timestamps so signatures do not show "December 31, 1969". */
export function isValidSignatureDate(value) {
  if (value == null || value === '') {
    return false;
  }
  const str = String(value);
  if (str.includes('1969') || str.startsWith('0000') || str === 'Invalid date') {
    return false;
  }
  return true;
}

/**
 * Resolve which signatures to display without changing sign-off workflow.
 * Clinician line only when clinician actually signed; supervisor from actual signer.
 */
/**
 * Supervisor sign-off: return to clinician (signed=4) unless clinician already fully signed.
 */
export function resolveSupervisorSignOffStatus(currentSigned) {
  if (Number(currentSigned) === 2) {
    return { signed: 2, supervisor_signed: 2 };
  }
  return { signed: 4, supervisor_signed: 2 };
}

export function formatSignatureLine(text, at) {
  const base = `${text}, signed this note and declared this information to be accurate and complete`;
  return isValidSignatureDate(at) ? `${base} on ${at}` : `${base}.`;
}

export function resolveDocumentSignatures({
  signed,
  supervisor_signed: supervisorSigned,
  signature,
  supervisor_signature: supervisorSignature,
  signed_at: signedAt,
  supervisor_signed_at: supervisorSignedAt,
  status_updated_by_role: statusUpdatedByRole,
  created_by: createdBy,
  supervisor_signed_by: supervisorSignedBy,
}) {
  const signedNum =
    normalizeDocumentSignStatus(signed) ?? Number(signed);
  const supervisorSignedNum =
    normalizeDocumentSignStatus(supervisorSigned) ?? Number(supervisorSigned);

  const clinician = {
    show: signedNum === 2 && Boolean(signature),
    text: signature || '',
    at: signedAt || '',
  };
  const supervisor = {
    show: supervisorSignedNum === 2 && Boolean(supervisorSignature),
    text: supervisorSignature || '',
    at: supervisorSignedAt || '',
  };

  // Author-only sign (2/1) — never show a supervisor line.
  if (signedNum === 2 && supervisorSignedNum === 1) {
    supervisor.show = false;
  }

  // Mistaken 2/2 author-only rows — no supervisor actually signed.
  if (
    signedNum === 2 &&
    supervisorSignedNum === 2 &&
    !Number(supervisorSignedBy) &&
    !isValidSignatureDate(supervisorSignedAt)
  ) {
    supervisor.show = false;
  }

  // Supervisor signed, author final sign pending (4/2) — supervisor line only until author signs.
  if (signedNum === 4 && supervisorSignedNum === 2) {
    clinician.show = false;
  }

  // Fully signed (2/2) — same person recorded as both author and supervisor: one line only.
  if (signedNum === 2 && supervisorSignedNum === 2 && clinician.show && supervisor.show) {
    const authorId = Number(createdBy);
    const supervisorId = Number(supervisorSignedBy);
    if (authorId && supervisorId && authorId === supervisorId) {
      supervisor.show = false;
    } else {
      const authorKey = clinician.text.trim().toLowerCase();
      const supKey = supervisor.text.trim().toLowerCase();
      if (authorKey && supKey && authorKey === supKey) {
        supervisor.show = false;
      }
    }
  }

  return { clinician, supervisor };
}

export function buildSignatureParagraphHtml(signatures) {
  const { clinician, supervisor } = signatures;
  let html = '';
  if (clinician.show) {
    html += `<p>${formatSignatureLine(clinician.text, clinician.at)}</p>`;
  }
  if (supervisor.show) {
    html += `<p>${formatSignatureLine(supervisor.text, supervisor.at)}</p>`;
  }
  return html;
}

/** Remove embedded signature lines from note HTML so view modal does not duplicate them. */
export function stripEmbeddedSignaturesFromHtml(html) {
  if (!html || typeof html !== 'string') {
    return html || '';
  }
  let out = html;
  let prev = '';
  while (out !== prev) {
    prev = out;
    out = out.replace(
      /<p[^>]*>[\s\S]*?signed this note and declared this information to be accurate and complete[\s\S]*?<\/p>/gi,
      ''
    );
    out = out.replace(
      /<div[^>]*>[\s\S]*?signed this note and declared this information to be accurate and complete[\s\S]*?<\/div>/gi,
      ''
    );
    out = out.replace(
      /[^<\n]*signed this note and declared this information to be accurate and complete\.?(\s+on\s+[^<\n]+)?/gi,
      ''
    );
  }
  out = out.replace(/<p[^>]*>\s*<\/p>/gi, '');
  out = out.replace(/<div[^>]*>\s*<\/div>/gi, '');
  return out;
}

/** New unsaved notes keep created_by as 0 — treat current user as author (production behaviour). */
export function resolveDocumentCreatedBy(storedCreatedBy, sessionUserId) {
  if (storedCreatedBy != null && Number(storedCreatedBy) !== 0) {
    return storedCreatedBy;
  }
  return sessionUserId ?? storedCreatedBy ?? 0;
}

/** True when the logged-in user authored the document (sign-off / final-sign flows). */
export function isDocumentAuthorForSignOff(
  userId,
  documentCreatedBy,
  userRoleSlug
) {
  if (userId == null) {
    return false;
  }
  if (userRoleSlug === 'supervisor') {
    return (
      documentCreatedBy != null &&
      Number(documentCreatedBy) !== 0 &&
      String(documentCreatedBy) === String(userId)
    );
  }
  if (userRoleSlug === 'clinician' || isPracticeAdminRole(userRoleSlug)) {
    if (
      documentCreatedBy != null &&
      Number(documentCreatedBy) !== 0 &&
      String(documentCreatedBy) === String(userId)
    ) {
      return true;
    }
    if (documentCreatedBy == null || Number(documentCreatedBy) === 0) {
      return (
        String(resolveDocumentCreatedBy(documentCreatedBy, userId)) ===
        String(userId)
      );
    }
  }
  return false;
}

/** True when the logged-in user wrote the document (not only the client record owner). */
export function isDocumentAuthorUser(
  userId,
  documentCreatedBy,
  clientCreatorId,
  userRoleSlug
) {
  if (isDocumentAuthorForSignOff(userId, documentCreatedBy, userRoleSlug)) {
    return true;
  }
  if (userId == null || userRoleSlug !== 'clinician') {
    return false;
  }
  const authorId = resolveDocumentCreatedBy(
    documentCreatedBy ?? clientCreatorId,
    userId
  );
  return String(userId) === String(authorId);
}

export function normalizeDocumentSignStatus(value) {
  if (value == null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Draft / in-progress rows (signed 1/1 or unset) — always editable in the note editor. */
export function isDocumentInProgressStatus(signed, supervisorSigned) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  return (
    (signedNum == null || signedNum === 0 || signedNum === 1) &&
    (supNum == null || supNum === 0 || supNum === 1)
  );
}

/** Prefer numeric status fields from list/detail APIs over badge objects. */
export function resolveDocumentSignStatusesFromApi(data = {}) {
  const signed =
    normalizeDocumentSignStatus(data.signed_status ?? data.signed) ?? 1;
  const supervisorSigned =
    normalizeDocumentSignStatus(
      data.supervisor_signed_status ?? data.supervisor_signed
    ) ?? 1;
  return { signed, supervisorSigned };
}

/** Author-only sign complete (no supervisor workflow). */
export function isDocumentAuthorSignedComplete(signed, supervisorSigned) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  return signedNum === 2 && (supNum === 1 || supNum === 2);
}

/** Document list supervisor column — hide misleading "In Progress" for 2/1 author-only sign. */
export function resolveDocumentListSupervisorSignedBadge(row = {}) {
  const signedNum = normalizeDocumentSignStatus(
    row.signed_status ?? row.signed
  );
  const supNum = normalizeDocumentSignStatus(
    row.supervisor_signed_status ?? row.supervisor_signed
  );
  if (signedNum === 2 && supNum === 1) {
    return { color: 'success', name: 'Signed' };
  }
  return row.supervisor_signed ?? { color: 'success', name: 'Signed' };
}

export function isDocumentFullySigned(signed, supervisorSigned) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  return signedNum === 2 && supNum === 2;
}

export function isClinicianSignOffPendingStatus(signed, supervisorSigned) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  return signedNum === 4 && supNum === 2;
}

/** Mirrors backend: no content edits after sign-off or while awaiting review. */
export function isDocumentContentLocked(signed, supervisorSigned, roleSlug = null) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  if (signedNum == null || supNum == null) {
    return false;
  }
  const authorRoles = ['clinician', 'practice_administrator', 'account'];
  if (signedNum === 2 && supNum === 1) {
    return true;
  }
  if (signedNum === 2 && supNum === 2) {
    return true;
  }
  if (signedNum === 4 && supNum === 2) {
    return true;
  }
  if (signedNum === 3 && supNum === 4 && authorRoles.includes(roleSlug)) {
    return true;
  }
  return false;
}

export function documentTypeSlugFromPageId(pageId) {
  const map = {
    1: 'progress_note',
    3: 'assessment',
    4: 'treatment_plan',
    5: 'discharge_summary',
  };
  return map[Number(pageId)] || '';
}

export function isPracticeAdminRole(roleSlug) {
  return roleSlug === 'account' || roleSlug === 'practice_administrator';
}

export function canUserManageClientDocument(row, userId, clientCreatorId, roleSlug) {
  if (isPracticeAdminRole(roleSlug)) {
    return true;
  }
  return (
    row?.created_by == userId ||
    row?.supervisor_id == userId ||
    row?.client_creator_id == userId ||
    clientCreatorId == userId
  );
}

/** Document list: who may open the note editor. Sign-off uses view modal, not the editor. */
export function canUserEditClientDocumentList(row, userId, clientCreatorId, roleSlug) {
  if (!canUserManageClientDocument(row, userId, clientCreatorId, roleSlug)) {
    return false;
  }
  const signedNum = normalizeDocumentSignStatus(
    row?.signed_status ?? row?.signed
  );
  const supNum = normalizeDocumentSignStatus(
    row?.supervisor_signed_status ?? row?.supervisor_signed
  );
  if (isDocumentContentLocked(signedNum, supNum, roleSlug)) {
    return false;
  }
  return true;
}

/** Clinician or PA author final sign from the document list view modal. */
export function canUserSignClientDocumentFromView(
  signed,
  supervisorSigned,
  userRoleSlug,
  userId,
  clientCreatorId,
  documentCreatedBy = null
) {
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  if (!isClinicianSignOffPendingStatus(signedNum, supNum)) {
    return false;
  }
  return isDocumentAuthorForSignOff(userId, documentCreatedBy, userRoleSlug);
}

/** Inside a note: Save / manual edit / keyword changes allowed? */
export function isDocumentContentEditable(
  signed,
  supervisorSigned,
  userRoleSlug,
  userId,
  clientCreatorId
) {
  if (isDocumentContentLocked(signed, supervisorSigned, userRoleSlug)) {
    return false;
  }
  const signedNum = normalizeDocumentSignStatus(signed);
  const supNum = normalizeDocumentSignStatus(supervisorSigned);
  if (userRoleSlug === 'supervisor' && signedNum === 4 && supNum === 2) {
    return false;
  }
  if (isPracticeAdminRole(userRoleSlug)) {
    if (signedNum == null || signedNum === 1) {
      return true;
    }
    return signedNum === 3 && supNum === 4;
  }
  return true;
}

/** Signed/locked doc in note editor — hide keyword form. In-progress rows stay editable. */
export function isDocumentViewOnlyInEditor({
  editId = 0,
  documentMetaLoaded = true,
  signed,
  supervisorSigned,
  userRoleSlug,
  userId,
  clientCreatorId,
}) {
  const contentEditable = isDocumentContentEditable(
    signed,
    supervisorSigned,
    userRoleSlug,
    userId,
    clientCreatorId
  );
  return (
    editId > 0 &&
    documentMetaLoaded &&
    !isDocumentInProgressStatus(signed, supervisorSigned) &&
    !contentEditable
  );
}

/** Shared editor access flags for Progress Note / Assessment / TP / Discharge Summary. */
export function resolveDocumentEditorAccess({
  editId = 0,
  documentMetaLoaded = true,
  signed,
  supervisorSigned,
  userRoleSlug,
  userId,
  clientCreatorId,
}) {
  const contentEditable = isDocumentContentEditable(
    signed,
    supervisorSigned,
    userRoleSlug,
    userId,
    clientCreatorId
  );
  const isViewOnlyDocument = isDocumentViewOnlyInEditor({
    editId,
    documentMetaLoaded,
    signed,
    supervisorSigned,
    userRoleSlug,
    userId,
    clientCreatorId,
  });
  return {
    contentEditable,
    isViewOnlyDocument,
    showDocumentEditActions: !isViewOnlyDocument,
    lockedForSave: isViewOnlyDocument,
  };
}

export function buildStoreClientDocumentViewEditFlag(confirmChange) {
  return confirmChange ? 1 : 0;
}

/** Author may open note only to apply final signature (no content edits). */
export function isClinicianFinalSignOnly(
  signed,
  supervisorSigned,
  userRoleSlug,
  userId,
  clientCreatorId,
  documentCreatedBy = null
) {
  if (!isClinicianSignOffPendingStatus(signed, supervisorSigned)) {
    return false;
  }
  return isDocumentAuthorForSignOff(userId, documentCreatedBy, userRoleSlug);
}

export function resolveSignOffButtonState({
  signed,
  supervisorSigned,
  userRoleSlug,
  userSupervisorId,
  documentCreatedBy = null,
  userId = null,
  clientSupervisorId = null,
}) {
  const signedNum = Number(signed);
  const supNum = Number(supervisorSigned);
  const isDocumentAuthor = isDocumentAuthorForSignOff(
    userId,
    documentCreatedBy,
    userRoleSlug
  );
  // Same rule as clinician: use the logged-in author's supervisor, not the client record creator's.
  const hasAuthorSupervisor = Boolean(userSupervisorId);

  if (isDocumentFullySigned(signedNum, supNum)) {
    return { checkSigned: 2, supervisorSigned: 2, showSigned: 0 };
  }

  if (userRoleSlug === 'clinician') {
    if (userSupervisorId) {
      if (supNum === 2 && signedNum !== 2) {
        return { checkSigned: 2, supervisorSigned: 2, showSigned: 1 };
      }
      if (signedNum !== 2 && supNum !== 2) {
        if (signedNum === 1) {
          return { checkSigned: 3, supervisorSigned: 4, showSigned: 1 };
        }
        return { checkSigned: 3, supervisorSigned: 4, showSigned: 0 };
      }
      return { checkSigned: 2, supervisorSigned: 2, showSigned: 0 };
    }
    return { checkSigned: 2, supervisorSigned: 1, showSigned: 1 };
  }

  if (userRoleSlug === 'supervisor') {
    if (signedNum === 4 && supNum === 2) {
      if (isDocumentAuthor) {
        return { checkSigned: 2, supervisorSigned: 2, showSigned: 1 };
      }
      return { checkSigned: 2, supervisorSigned: 2, showSigned: 0 };
    }
    // Supervisor wrote this note — one-step sign (2/2), not return-to-author (4/2).
    if (
      isDocumentAuthor &&
      (signedNum === 1 || signedNum === 3) &&
      (supNum === 1 || supNum === 4)
    ) {
      return { checkSigned: 2, supervisorSigned: 2, showSigned: 1 };
    }
    const supervisorSign = resolveSupervisorSignOffStatus(signedNum);
    return {
      checkSigned: supervisorSign.signed,
      supervisorSigned: supervisorSign.supervisor_signed,
      showSigned: 1,
    };
  }

  if (isPracticeAdminRole(userRoleSlug)) {
    if (signedNum === 4 && supNum === 2) {
      if (isDocumentAuthor) {
        return { checkSigned: 2, supervisorSigned: 2, showSigned: 1 };
      }
      return { checkSigned: 2, supervisorSigned: 2, showSigned: 0 };
    }
    if (signedNum === 3 && supNum === 4) {
      if (isDocumentAuthor && hasAuthorSupervisor) {
        return { checkSigned: 3, supervisorSigned: 4, showSigned: 0 };
      }
      return { checkSigned: 4, supervisorSigned: 2, showSigned: 1 };
    }
    if (signedNum === 1 && supNum === 1 && isDocumentAuthor) {
      if (hasAuthorSupervisor) {
        return { checkSigned: 3, supervisorSigned: 4, showSigned: 1 };
      }
      return { checkSigned: 2, supervisorSigned: 1, showSigned: 1 };
    }
    if (isDocumentAuthorSignedComplete(signedNum, supNum)) {
      return { checkSigned: 2, supervisorSigned: 1, showSigned: 0 };
    }
    return { checkSigned: 2, supervisorSigned: 2, showSigned: 0 };
  }

  return { checkSigned: 0, supervisorSigned: 0, showSigned: 0 };
}

/** Note edit page sign button label — Sign Request only when sending to supervisor (3/4). */
export function getSignOffButtonLabel(checkSigned) {
  return Number(checkSigned) === 3 ? 'Sign Request' : 'Sign Doc';
}

/** Confirm modal wording on note edit page. */
export function getSignOffConfirmPrompt(checkSigned) {
  return Number(checkSigned) === 3 ? 'send Sign Request' : 'Sign Doc';
}

function mergeSentenceBuilderInputFromElement(data, el) {
  const name = el.name?.trim();
  if (!name) {
    return;
  }
  if (el.type === 'checkbox') {
    const fieldValue = el.value || '';
    if (fieldValue.includes('_addNewArray')) {
      let parsed;
      try {
        parsed = { ...JSON.parse(el.id), checked: el.checked };
      } catch {
        return;
      }
      const arrayKey = fieldValue;
      const existing = Array.isArray(data[arrayKey]) ? data[arrayKey] : [];
      data[arrayKey] = [...existing, parsed];
      return;
    }
    data[name] = el.checked;
    return;
  }
  if (el.type === 'radio') {
    const fieldValue = el.value || '';
    if (fieldValue.includes('_addNewArray')) {
      if (!el.checked) {
        return;
      }
      let parsed;
      try {
        parsed = { ...JSON.parse(el.id), checked: el.checked };
      } catch {
        return;
      }
      const arrayKey = fieldValue;
      const existing = Array.isArray(data[arrayKey]) ? data[arrayKey] : [];
      data[arrayKey] = [...existing, parsed];
      return;
    }
    if (el.checked) {
      data[el.id || name] = true;
    }
    return;
  }
  if (el.type === 'select-one') {
    data[name] = el.value;
    return;
  }
  if (name.includes('notes') || el.tagName === 'TEXTAREA') {
    data[name] = el.value;
  }
}

function domHasLiveCheckboxSnapshot(domData = {}) {
  return Object.keys(domData).some((key) => {
    const value = domData[key];
    return value === true || value === false;
  });
}

/** Prefix shared by toggle keys, e.g. mse_appearance → mse_ */
export function deriveToggleSectionPrefix(keys = []) {
  const first = keys.find((key) => typeof key === 'string' && key.includes('_'));
  if (!first) {
    return '';
  }
  const idx = first.indexOf('_');
  return idx > 0 ? `${first.slice(0, idx)}_` : '';
}

/** Whether a DOM control belongs to a sentence-builder section (checkbox, radio, text, notes, select). */
export function fieldMatchesSentenceBuilderSectionPrefix(el, prefix) {
  if (!el || !prefix) {
    return false;
  }
  const id = typeof el.id === 'string' ? el.id : '';
  const name = typeof el.name === 'string' ? el.name : '';
  const value = typeof el.value === 'string' ? el.value : '';
  if (id.startsWith(prefix) || name.startsWith(prefix)) {
    return true;
  }
  if (value.startsWith(prefix)) {
    return true;
  }
  if (id.startsWith('{') && value.includes(prefix)) {
    return true;
  }
  if (name.includes('notes') && name.includes(prefix.replace(/_$/, ''))) {
    return true;
  }
  return false;
}

/** Reset one sentence-builder control (checkbox, radio, text, textarea, select). */
export function resetSentenceBuilderFormElement(el) {
  if (!el) {
    return;
  }
  const type = (el.type || '').toLowerCase();
  const tag = (el.tagName || '').toUpperCase();
  if (type === 'checkbox' || type === 'radio') {
    el.checked = false;
    return;
  }
  if (type === 'select-one' || tag === 'SELECT') {
    el.selectedIndex = 0;
    return;
  }
  if (type === 'hidden' || type === 'button' || type === 'submit') {
    return;
  }
  if (
    type === 'text' ||
    type === 'textarea' ||
    type === 'search' ||
    tag === 'TEXTAREA'
  ) {
    el.value = '';
  }
}

/** Clear every sentence-builder field under a section prefix (MSE all-normal off, etc.). */
export function clearSentenceBuilderSectionFields(prefix, root = null) {
  const formRoot =
    root ||
    (typeof document !== 'undefined'
      ? document.getElementById('notenest-accordion-form')
      : null);
  if (!formRoot || !prefix) {
    return;
  }
  formRoot.querySelectorAll('input, select, textarea').forEach((el) => {
    if (fieldMatchesSentenceBuilderSectionPrefix(el, prefix)) {
      resetSentenceBuilderFormElement(el);
    }
  });
  // Do not toggle .collapse / .show here — React Bootstrap Collapse owns visibility.
}

/**
 * Collect current accordion form values for sentence-builder (Assessment / Progress Note).
 */
export function collectSentenceBuilderFormDataFromDom() {
  if (typeof document === 'undefined') {
    return {};
  }

  const roots = [
    document.querySelector('#notenest-accordion-form'),
    document.querySelector('#statementComponent__div_id_as'),
    document.querySelector('#statementComponent__div_id_pn'),
    document.querySelector('#statementComponent__div_id_tp'),
    document.querySelector('#statementComponent__div_id_ds'),
    document.querySelector('.statementComponent__div'),
  ].filter(Boolean);

  if (roots.length === 0) {
    return {};
  }

  const data = {};
  roots.forEach((root) => {
    root.querySelectorAll('input, select, textarea').forEach((el) => {
      mergeSentenceBuilderInputFromElement(data, el);
    });
  });
  return data;
}

function resolveSentenceBuilderBasePayload(payloadData, editData) {
  if (payloadData && typeof payloadData === 'object') {
    const keys = Object.keys(payloadData);
    if (keys.length > 0) {
      return payloadData;
    }
  }
  if (editData && typeof editData === 'object' && editData !== false) {
    return editData;
  }
  return {};
}

/** Full checkbox/form state for store-client-document (not highlight-only Data). */
export function buildDocumentSavePayload({
  payloadData = null,
  editData = null,
  extras = {},
} = {}) {
  const base = resolveSentenceBuilderBasePayload(payloadData, editData);
  const domData = collectSentenceBuilderFormDataFromDom();
  return {
    ...base,
    ...domData,
    ...extras,
  };
}

const ASSESSMENT_DRAFT_STORAGE_PREFIX = 'notenest_assessment_draft_';

export function getAssessmentDraftStorageKey(clientId) {
  return `${ASSESSMENT_DRAFT_STORAGE_PREFIX}${clientId}`;
}

export function rememberAssessmentDraftId(clientId, documentId) {
  if (clientId && documentId) {
    sessionStorage.setItem(
      getAssessmentDraftStorageKey(clientId),
      String(documentId)
    );
  }
}

export function readAssessmentDraftId(clientId) {
  const raw = sessionStorage.getItem(getAssessmentDraftStorageKey(clientId));
  const id = Number(raw);
  return Number.isNaN(id) || id <= 0 ? 0 : id;
}

export function clearAssessmentDraftId(clientId) {
  sessionStorage.removeItem(getAssessmentDraftStorageKey(clientId));
}

/**
 * Build sentence-builder body so SESSION DETAILS update the preview even before
 * any accordion checkbox is selected (payloadData may still be empty).
 */
export function buildCustomPracticeSentenceRequestPayload({
  payloadData = null,
  editData = null,
  userInfo,
  viewEdit,
  commonData,
  customPracticeMeta,
  metaOverride = null,
  sessionDuration = '',
}) {
  const meta = {
    ...customPracticeMeta,
    ...(metaOverride || {}),
  };
  const domData = collectSentenceBuilderFormDataFromDom();
  const base = resolveSentenceBuilderBasePayload(
    payloadData,
    editData ?? viewEdit?.editData
  );
  const merged = { ...base, ...domData };
  // Drop stale checked keys only when the accordion form is mounted and readable.
  if (domHasLiveCheckboxSnapshot(domData)) {
    Object.keys(base).forEach((key) => {
      if (base[key] === true && !(key in domData)) {
        delete merged[key];
      }
    });
  }
  return {
    ...merged,
    client_id: userInfo?.id ?? payloadData?.client_id,
    client_document_id:
      viewEdit?.client_document_id ?? payloadData?.client_document_id,
    view_edit_flag: payloadData?.view_edit_flag ?? 0,
    notes_id: viewEdit?.notes_id ?? payloadData?.notes_id ?? 0,
    date_of_service:
      commonData?.date_of_service ?? payloadData?.date_of_service,
    highlightedWordsId: payloadData?.highlightedWordsId ?? '',
    highlightedWords: payloadData?.highlightedWords ?? '',
    sessionDuration:
      sessionDuration !== '' && sessionDuration != null
        ? sessionDuration
        : payloadData?.sessionDuration ?? '',
    ...meta,
  };
}

function formatTimeLabel(hour24, minute) {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const min = minute.toString().padStart(2, '0');
  return `${hour12}:${min} ${period}`;
}

/** 12:00 AM – 11:59 PM, every minute */
export function buildCustomPracticeTimeOptions() {
  const options = [];
  for (let h = 0; h <= 23; h += 1) {
    for (let m = 0; m < 60; m += 1) {
      const value = formatTimeLabel(h, m);
      options.push({ value, label: value });
    }
  }
  return options;
}

export const CUSTOM_PRACTICE_TIME_OPTIONS = buildCustomPracticeTimeOptions();

function ensureCurrentInOptions(options, currentValue = '') {
  if (!currentValue) {
    return options;
  }
  if (options.some((opt) => opt.value === currentValue)) {
    return options;
  }
  return [{ value: currentValue, label: currentValue }, ...options];
}

/** Parse "9:00 AM" style label to minutes from midnight */
export function parseTimeLabelToMinutes(timeLabel) {
  if (!timeLabel) {
    return null;
  }
  const match = String(timeLabel)
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  }
  if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  return hour * 60 + minute;
}

/** Fixed date — time-only fields are not tied to the current calendar day */
const TIME_PICKER_REFERENCE_DATE = new Date(2000, 0, 1, 0, 0, 0, 0);

/** Date for MUI TimePicker from "9:00 AM" label (not today's date) */
export function parseTimeLabelToDate(timeLabel) {
  const totalMin = parseTimeLabelToMinutes(timeLabel);
  if (totalMin === null) {
    return null;
  }
  const date = new Date(TIME_PICKER_REFERENCE_DATE);
  date.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
  return date;
}

/** "9:00 AM" label from Date (MUI TimePicker onChange) */
export function formatDateToTimeLabel(date) {
  if (!date || Number.isNaN(date.getTime?.())) {
    return '';
  }
  return formatTimeLabel(date.getHours(), date.getMinutes());
}

const CUSTOM_PRACTICE_MIN_TIME_MINUTES = 0;
const CUSTOM_PRACTICE_MAX_TIME_MINUTES = 23 * 60 + 59;

function minutesFromMidnightToDate(totalMin) {
  const date = new Date(TIME_PICKER_REFERENCE_DATE);
  date.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
  return date;
}

/** Full day (12:00 AM – 11:59 PM). Not tied to other field or current day. */
export function getCustomPracticeTimePickerBounds() {
  return {
    minTime: minutesFromMidnightToDate(CUSTOM_PRACTICE_MIN_TIME_MINUTES),
    maxTime: minutesFromMidnightToDate(CUSTOM_PRACTICE_MAX_TIME_MINUTES),
  };
}

export function isEndTimeAfterStartTime(startTime, endTime) {
  const startMin = parseTimeLabelToMinutes(startTime);
  const endMin = parseTimeLabelToMinutes(endTime);
  if (startMin === null || endMin === null) {
    return true;
  }
  return endMin > startMin;
}

/** User-facing validation: start and end must differ (end after start). */
export function getCustomPracticeTimeValidationError(startTime, endTime) {
  const startMin = parseTimeLabelToMinutes(startTime);
  const endMin = parseTimeLabelToMinutes(endTime);
  if (startMin === null || endMin === null) {
    return '';
  }
  if (startMin === endMin) {
    return 'Start time and end time must be different.';
  }
  if (endMin < startMin) {
    return 'End time must be after start time.';
  }
  return '';
}

/** Minutes between start and end labels; null if either time is missing or invalid. */
export function computeSessionDurationMinutesFromTimes(startTime, endTime) {
  const startMin = parseTimeLabelToMinutes(startTime);
  const endMin = parseTimeLabelToMinutes(endTime);
  if (startMin === null || endMin === null || endMin <= startMin) {
    return null;
  }
  return endMin - startMin;
}

/** True when both times are set and session duration should be auto-calculated (read-only). */
export function isSessionDurationCalculatedFromTimes(meta = {}) {
  return computeSessionDurationMinutesFromTimes(meta.start_time, meta.end_time) !== null;
}

/** Start and/or end time not set — duration defaults to 0 until both are chosen. */
export function isCustomPracticeSessionTimesIncomplete(meta = {}) {
  return (
    !String(meta.start_time ?? '').trim() ||
    !String(meta.end_time ?? '').trim()
  );
}

/** Prefer duration derived from start/end in meta (avoids stale React state in preview API). */
export function resolveSessionDurationForPreview(meta = {}, fallbackDuration = '') {
  const fromTimes = computeSessionDurationMinutesFromTimes(
    meta.start_time,
    meta.end_time
  );
  if (fromTimes !== null) {
    return String(fromTimes);
  }
  if (isCustomPracticeSessionTimesIncomplete(meta)) {
    return '0';
  }
  if (fallbackDuration !== '' && fallbackDuration != null) {
    return String(fallbackDuration);
  }
  return '0';
}

/** Minutes shown in the read-only session duration field (times first, then stored). */
export function resolveCustomPracticeDisplaySessionMinutes(meta = {}, storedMinutes = '') {
  return resolveSessionDurationForPreview(meta, storedMinutes);
}

/** Keeps user-selected times; validation UI handles invalid pairs. Computes duration when valid. */
export function applyCustomPracticeTimeRules(meta, changedField = null) {
  const next = { ...meta };

  if (!next.start_time) {
    next.end_time = '';
  } else if (
    changedField === 'start_time' &&
    next.end_time &&
    computeSessionDurationMinutesFromTimes(next.start_time, next.end_time) === null
  ) {
    next.end_time = '';
  }

  const durationMinutes = computeSessionDurationMinutesFromTimes(
    next.start_time,
    next.end_time
  );
  if (durationMinutes !== null && changedField !== 'ref_id' && changedField !== 'ins_id') {
    next._computed_session_duration_minutes = durationMinutes;
  } else if (isCustomPracticeSessionTimesIncomplete(next)) {
    next._computed_session_duration_minutes = 0;
  } else {
    delete next._computed_session_duration_minutes;
  }

  return next;
}

/** Apply a single session-header field change (avoids stale React state merges). */
export function applyCustomPracticeFieldChange(prevMeta, field, value) {
  const nextRaw = applyCustomPracticeTimeRules(
    { ...prevMeta, [field]: value },
    field
  );
  let computed = nextRaw._computed_session_duration_minutes;
  if (computed === undefined && isCustomPracticeSessionTimesIncomplete(nextRaw)) {
    computed = 0;
  }
  const next = { ...nextRaw };
  delete next._computed_session_duration_minutes;
  return { next, computed };
}

/** Prefill session header from client profile (ref / Medicaid). */
export function getCustomPracticeMetaFromClient(userInfo = {}) {
  return {
    ...CUSTOM_PRACTICE_META_DEFAULTS,
    ref_id: userInfo.ref_id ?? '',
    ins_id: userInfo.ins_id ?? '',
  };
}

/** Session header values for progress note preview (all accounts). */
export function mergeProgressNoteSessionMetaFromDoc(doc, userInfo = {}) {
  const base = getCustomPracticeMetaFromClient(userInfo);
  const merged = !doc
    ? base
    : {
        ...base,
        start_time: doc.start_time ?? '',
        end_time: doc.end_time ?? '',
        place_of_service:
          doc.place_of_service ?? CUSTOM_PRACTICE_META_DEFAULTS.place_of_service,
        method_of_contact:
          doc.method_of_contact ?? CUSTOM_PRACTICE_META_DEFAULTS.method_of_contact,
        ref_id: doc.ref_id ?? userInfo.ref_id ?? '',
        ins_id: doc.ins_id ?? userInfo.ins_id ?? '',
      };
  const next = applyCustomPracticeTimeRules(merged);
  delete next._computed_session_duration_minutes;
  return next;
}

export function getStartTimeSelectOptions(meta = {}, currentValue = '') {
  const endMin = parseTimeLabelToMinutes(meta.end_time);
  let options = CUSTOM_PRACTICE_TIME_OPTIONS;
  if (endMin !== null) {
    options = options.filter(
      (opt) => parseTimeLabelToMinutes(opt.value) < endMin
    );
  }
  return ensureCurrentInOptions(options, currentValue || meta.start_time);
}

export function getEndTimeSelectOptions(meta = {}, currentValue = '') {
  const startMin = parseTimeLabelToMinutes(meta.start_time);
  let options = CUSTOM_PRACTICE_TIME_OPTIONS;
  if (startMin !== null) {
    options = options.filter(
      (opt) => parseTimeLabelToMinutes(opt.value) > startMin
    );
  }
  return ensureCurrentInOptions(options, currentValue || meta.end_time);
}

/** iOS-style wheel column order: 12, 1, 2, … 11 */
export const CUSTOM_PRACTICE_WHEEL_HOURS = [
  '12',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
];

/** 00–59 for minute picker / datalist */
export const CUSTOM_PRACTICE_WHEEL_MINUTES = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0')
);

export const CUSTOM_PRACTICE_WHEEL_PERIODS = ['AM', 'PM'];

export function normalizeHour12Input(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) {
    return '';
  }
  if (n < 1) {
    return '1';
  }
  if (n > 12) {
    return '12';
  }
  return String(n);
}

export function normalizeMinuteInput(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) {
    return '';
  }
  if (n < 0) {
    return '00';
  }
  if (n > 59) {
    return '59';
  }
  return String(n).padStart(2, '0');
}

export function parseTimeLabelToParts(timeLabel) {
  if (!timeLabel) {
    return { hour12: '', minute: '', period: '' };
  }
  const totalMin = parseTimeLabelToMinutes(timeLabel);
  if (totalMin === null) {
    return { hour12: '', minute: '', period: '' };
  }
  const period = totalMin >= 12 * 60 ? 'PM' : 'AM';
  let hour24 = Math.floor(totalMin / 60);
  const minute = (totalMin % 60).toString().padStart(2, '0');
  let hour12 = hour24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }
  return { hour12: String(hour12), minute, period };
}

/** Current clock time as wheel picker parts (hour 1–12, minute 00–59, AM/PM). */
export function getCurrentTimeWheelParts(date = new Date()) {
  const hour24 = date.getHours();
  const minute = String(date.getMinutes()).padStart(2, '0');
  const period = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }
  return { hour12: String(hour12), minute, period };
}

export function buildTimeLabelFromParts(hour12, minute, period) {
  if (!hour12 || !minute || !period) {
    return '';
  }
  let hour = parseInt(hour12, 10);
  const min = parseInt(minute, 10);
  if (Number.isNaN(hour) || Number.isNaN(min)) {
    return '';
  }
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  }
  if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  return formatTimeLabel(hour, min);
}

export function getTimeSelectOptionsForField(fieldKey, meta = {}, currentValue = '') {
  if (fieldKey === 'start_time') {
    return getStartTimeSelectOptions(meta, currentValue);
  }
  if (fieldKey === 'end_time') {
    return getEndTimeSelectOptions(meta, currentValue);
  }
  return [];
}

/** Hours that appear in at least one allowed time option */
export function getWheelHoursFromOptions(options) {
  const set = new Set();
  options.forEach((opt) => {
    const p = parseTimeLabelToParts(opt.value);
    if (p.hour12) {
      set.add(p.hour12);
    }
  });
  return CUSTOM_PRACTICE_WHEEL_HOURS.filter((h) => set.has(h));
}

export function getSuggestedHoursFromOptions(options) {
  const filtered = getWheelHoursFromOptions(options);
  return filtered.length > 0 ? filtered : [...CUSTOM_PRACTICE_WHEEL_HOURS];
}

export function getSuggestedMinutesFromOptions(options, hour12, period) {
  if (!hour12 || !period) {
    return CUSTOM_PRACTICE_WHEEL_MINUTES;
  }
  const set = new Set();
  options.forEach((opt) => {
    const p = parseTimeLabelToParts(opt.value);
    if (p.hour12 === hour12 && p.period === period) {
      set.add(p.minute);
    }
  });
  const filtered = CUSTOM_PRACTICE_WHEEL_MINUTES.filter((m) => set.has(m));
  return filtered.length > 0 ? filtered : CUSTOM_PRACTICE_WHEEL_MINUTES;
}

/** @deprecated use getSuggestedMinutesFromOptions */
export function getWheelMinutesFromOptions(options, hour12, period) {
  return getSuggestedMinutesFromOptions(options, hour12, period);
}

/** Always show AM and PM; validation snaps to allowed window on save */
export function getWheelPeriodsFromOptions() {
  return [...CUSTOM_PRACTICE_WHEEL_PERIODS];
}

/** Snap to exact or nearest option in the filtered list (start/end rules). */
export function resolveWheelTimeSelection(options, hour12, minute, period) {
  const h = normalizeHour12Input(hour12);
  const m = normalizeMinuteInput(minute);
  const p = period === 'PM' ? 'PM' : period === 'AM' ? 'AM' : '';

  if (!h || !m || !p) {
    return '';
  }

  const direct = buildTimeLabelFromParts(h, m, p);
  if (direct && options.some((o) => o.value === direct)) {
    return direct;
  }

  const targetMin = parseTimeLabelToMinutes(direct);
  if (targetMin === null || options.length === 0) {
    return '';
  }

  let best = options[0];
  let bestDiff = Infinity;
  options.forEach((o) => {
    const optMin = parseTimeLabelToMinutes(o.value);
    if (optMin === null) {
      return;
    }
    const diff = Math.abs(optMin - targetMin);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = o;
    }
  });
  return best?.value ?? '';
}

export function getTimeSelectOptions(currentValue = '') {
  return ensureCurrentInOptions(CUSTOM_PRACTICE_TIME_OPTIONS, currentValue);
}

/**
 * Unique notesArr key per goal: objective_{notesId}_{goalIndex}_{objKey}
 * Legacy API keys objective_{notesId}_{objKey} are scoped with goalIndex.
 */
export function normalizeObjectiveNotesKey(fieldKey, goalIndex) {
  const key = String(fieldKey ?? '');
  const scoped = key.match(/^objective_(\d+)_(\d+)_(\d+)$/);
  if (scoped) {
    return key;
  }
  const legacy = key.match(/^objective_(\d+)_(\d+)$/);
  if (legacy) {
    return `objective_${legacy[1]}_${goalIndex}_${legacy[2]}`;
  }
  return `${key}_goal_${goalIndex}`;
}

export function objectiveNotesKeyToFormId(notesKey) {
  return `objectives_${notesKey}`;
}

/** Map legacy form keys to scoped checkbox ids */
export function normalizeCustomPracticeFrontJson(frontJson = {}) {
  if (!frontJson || typeof frontJson !== 'object') {
    return {};
  }
  const normalized = { ...frontJson };
  Object.keys(frontJson).forEach((key) => {
    if (!frontJson[key]) {
      return;
    }
    const doublePrefix = key.match(/^objectives_objectives_objective_(.+)$/);
    if (doublePrefix) {
      normalized[`objectives_objective_${doublePrefix[1]}`] = frontJson[key];
      return;
    }
    const legacyForm = key.match(/^objectives_objective_(\d+)_(\d+)$/);
    if (legacyForm) {
      normalized[`objectives_objective_${legacyForm[1]}_0_${legacyForm[2]}`] =
        frontJson[key];
      return;
    }
    const bareObjective = key.match(/^objective_(\d+)_(\d+)_(\d+)$/);
    if (bareObjective) {
      const notesId = bareObjective[1];
      const goalIndex = bareObjective[2];
      const objKey = bareObjective[3];
      normalized[
        `objectives_objective_${notesId}_${goalIndex}_${objKey}`
      ] = frontJson[key];
      normalized[
        `goals_goal_${notesId}_${goalIndex}_objective_${notesId}_${objKey}`
      ] = frontJson[key];
      return;
    }
    const bareLegacyObjective = key.match(/^objective_(\d+)_(\d+)$/);
    if (bareLegacyObjective) {
      normalized[
        `objectives_objective_${bareLegacyObjective[1]}_0_${bareLegacyObjective[2]}`
      ] = frontJson[key];
      return;
    }
    const assessStyleObjective = key.match(/^objectives_objective_(\d+)_(\d+)_(\d+)$/);
    if (assessStyleObjective) {
      const notesId = assessStyleObjective[1];
      const goalIndex = assessStyleObjective[2];
      const objKey = assessStyleObjective[3];
      normalized[
        `goals_goal_${notesId}_${goalIndex}_objective_${notesId}_${objKey}`
      ] = frontJson[key];
    }
  });
  return normalized;
}

function findFormCheckboxElement(fieldKey) {
  if (!fieldKey) {
    return null;
  }
  const byId = document.getElementById(fieldKey);
  if (byId) {
    return byId;
  }
  const escaped = String(fieldKey).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const byName = document.querySelector(`input[name="${escaped}"]`);
  if (byName) {
    return byName;
  }
  return null;
}

export function applyFrontJsonToCheckboxes(frontJson = {}) {
  const restored = normalizeCustomPracticeFrontJson(frontJson);
  let applied = 0;
  const expected = Object.keys(restored).filter((key) => restored[key]).length;

  Object.keys(restored).forEach((key) => {
    if (!restored[key]) {
      return;
    }
    const el = findFormCheckboxElement(key);
    if (el && (el.type === 'checkbox' || el.type === 'radio')) {
      el.checked = true;
      applied += 1;
    }
    const sectionCollapse = document.getElementById(`${key}Collapse`);
    if (sectionCollapse && restored[key]) {
      sectionCollapse.classList.add('show');
    }
    if (key.includes('_goal_') || key.startsWith('objectives_objective_')) {
      document.querySelectorAll(`.${key}collapse`).forEach((node) => {
        node.classList.add('show');
      });
      const collapse = document.getElementById(`${key}Collapse`);
      if (collapse) {
        collapse.classList.add('show');
      }
    }
  });

  return { applied, expected };
}

let checkboxRestoreEpoch = 0;

/** Call when the user changes a checkbox so pending restores are ignored. */
export function bumpCheckboxRestoreEpoch() {
  checkboxRestoreEpoch += 1;
  return checkboxRestoreEpoch;
}

export function scheduleFrontJsonCheckboxRestore(
  frontJson = {},
  attempt = 0,
  epoch = checkboxRestoreEpoch
) {
  const maxAttempts = 12;
  const run = () => {
    if (epoch !== checkboxRestoreEpoch) {
      return;
    }
    const { applied, expected } = applyFrontJsonToCheckboxes(frontJson);
    if (attempt < maxAttempts && expected > 0 && applied < expected) {
      setTimeout(
        () => scheduleFrontJsonCheckboxRestore(frontJson, attempt + 1, epoch),
        80 + attempt * 40
      );
    }
  };

  if (attempt === 0) {
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  } else {
    run();
  }
}

/** Debounce window for sentence-builder preview API calls (checkboxes + session fields) */
export const SENTENCE_BUILDER_DEBOUNCE_MS = 250;
/** Cap wait when user keeps clicking — still refresh preview without feeling stuck */
export const SENTENCE_BUILDER_MAX_WAIT_MS = 750;
/** Show preview loader only if API exceeds this threshold */
export const SENTENCE_BUILDER_LOADER_DELAY_MS = 350;
/** Abort hung sentence-builder requests so the queue never blocks the form */
export const SENTENCE_BUILDER_API_TIMEOUT_MS = 45000;
/** Debounce parent payload sync so checkbox clicks stay responsive */
export const FORM_SELECTION_SYNC_DEBOUNCE_MS = 120;

/** Single source of truth for session header fields (labels + property keys) */
export const CUSTOM_PRACTICE_HEADER_FIELDS = [
  {
    key: 'start_time',
    label: 'Start time',
    type: 'time-select',
    placeholder: 'Select start time',
  },
  {
    key: 'end_time',
    label: 'End time',
    type: 'time-select',
    placeholder: 'Select end time',
  },
  {
    key: 'place_of_service',
    label: 'Place of Service',
    type: 'select',
    placeholder: 'Select place of service',
    options: [
      { value: 'In-Home', label: 'In-Home' },
      { value: 'In-Community', label: 'In-Community' },
    ],
  },
  {
    key: 'method_of_contact',
    label: 'Method of contact',
    type: 'select',
    placeholder: 'Select method of contact',
    options: [
      { value: 'In-Person', label: 'In-Person' },
      { value: 'Telehealth', label: 'Telehealth' },
      { value: 'Telephone', label: 'Telephone' },
    ],
  },
  {
    key: 'ref_id',
    label: 'Ref ID / Record no',
    type: 'text',
    placeholder: 'Ref ID / Record no',
  },
  {
    key: 'ins_id',
    label: 'Ins ID / Medicaid',
    type: 'text',
    placeholder: 'Ins ID / Medicaid',
  },
];

export const CUSTOM_PRACTICE_FIELD_LABELS = {
  start_time: 'Start Time',
  end_time: 'End Time',
  place_of_service: 'Place of Service',
  method_of_contact: 'Method of Contact',
  ref_id: 'Ref ID / Record No',
  ins_id: 'Ins ID / Medicaid',
};

export const CUSTOM_PRACTICE_SESSION_META_KEYS =
  CUSTOM_PRACTICE_HEADER_FIELDS.map((field) => field.key);

/** Keep live header UI values when merging checkbox payloads (avoids stale meta overwrites). */
export function mergeLiveCustomPracticeSessionMeta(frontJson = {}, liveMeta = {}) {
  if (!liveMeta || typeof liveMeta !== 'object') {
    return frontJson;
  }
  const merged = { ...frontJson };
  CUSTOM_PRACTICE_SESSION_META_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(liveMeta, key)) {
      merged[key] = liveMeta[key];
    }
  });
  return merged;
}

/** DB stores total minutes; custom practice UI shows hours (same layout as Min field). */
export function minutesToHoursMinutes(totalMinutes) {
  const total = parseInt(totalMinutes, 10);
  if (Number.isNaN(total) || total < 0) {
    return { hours: "", minutes: "" };
  }
  return {
    hours: Math.floor(total / 60),
    minutes: total % 60,
  };
}

/** Default session length for custom practice (1 hour). */
export const CUSTOM_PRACTICE_DEFAULT_SESSION_DURATION_HOURS = '1';
export const CUSTOM_PRACTICE_DEFAULT_SESSION_DURATION_MINUTES = 60;

export function resolveSessionDurationMinutes(totalMinutes, useCustomPracticeDefault = true) {
  if (
    totalMinutes !== '' &&
    totalMinutes !== null &&
    totalMinutes !== undefined
  ) {
    const total = parseInt(totalMinutes, 10);
    if (!Number.isNaN(total) && total >= 0) {
      return total;
    }
  }
  return useCustomPracticeDefault
    ? CUSTOM_PRACTICE_DEFAULT_SESSION_DURATION_MINUTES
    : '';
}

export function minutesToHoursDisplay(totalMinutes) {
  const total = parseInt(totalMinutes, 10);
  if (Number.isNaN(total) || total <= 0) {
    return "";
  }
  const hrs = total / 60;
  return Number.isInteger(hrs) ? String(hrs) : String(parseFloat(hrs.toFixed(2)));
}

export function hoursToTotalMinutes(hours) {
  if (hours === "" || hours === null || hours === undefined) {
    return "";
  }
  const h = parseFloat(hours);
  if (Number.isNaN(h) || h < 0) {
    return "";
  }
  return Math.round(h * 60);
}

export function hoursMinutesToTotalMinutes(hours, minutes) {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  return h * 60 + m;
}

export function formatSessionDurationLabel(totalMinutes) {
  const total = parseInt(totalMinutes, 10);
  if (Number.isNaN(total) || total < 0) {
    return "";
  }
  if (total === 0) {
    return "0 Min";
  }
  const { hours, minutes } = minutesToHoursMinutes(total);
  const parts = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "Hour" : "Hours"}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} Min`);
  }
  return parts.join(" ") || "0 Min";
}

/**
 * Read-only session duration input: under 60 → total minutes + Min;
 * 60+ → hours + Hrs, and optional minutes + Min (e.g. 1 Hr 20 Min).
 */
export function getSessionDurationInputDisplay(totalMinutes) {
  const total = parseInt(totalMinutes, 10);
  if (Number.isNaN(total) || total < 0) {
    return {
      mainValue: "",
      mainUnit: "Min",
      subValue: "",
      subUnit: "",
      hiddenMinutes: "",
    };
  }
  if (total === 0) {
    return {
      mainValue: "0",
      mainUnit: "Min",
      subValue: "",
      subUnit: "",
      hiddenMinutes: "0",
    };
  }
  const { hours, minutes } = minutesToHoursMinutes(total);
  if (total < 60) {
    return {
      mainValue: String(total),
      mainUnit: "Min",
      subValue: "",
      subUnit: "",
      hiddenMinutes: String(total),
    };
  }
  if (minutes === 0) {
    return {
      mainValue: String(hours),
      mainUnit: hours === 1 ? "Hr" : "Hrs",
      subValue: "",
      subUnit: "",
      hiddenMinutes: String(total),
    };
  }
  return {
    mainValue: String(hours),
    mainUnit: hours === 1 ? "Hr" : "Hrs",
    subValue: String(minutes),
    subUnit: "Min",
    hiddenMinutes: String(total),
  };
}

/** Custom practice: total minutes for API / preview (use totalMinutesFromDurationParts for UI input). */
export function resolveCustomPracticeSessionDurationMinutes(
  storedMinutes,
  computedTotalMinutes
) {
  if (
    computedTotalMinutes !== "" &&
    computedTotalMinutes !== null &&
    computedTotalMinutes !== undefined
  ) {
    const total = Number(computedTotalMinutes);
    if (!Number.isNaN(total) && total > 0) {
      return total;
    }
  }
  if (
    storedMinutes !== "" &&
    storedMinutes !== null &&
    storedMinutes !== undefined
  ) {
    const total = parseInt(storedMinutes, 10);
    if (!Number.isNaN(total) && total > 0) {
      return total;
    }
  }
  return "";
}

/** Custom practice preview/sentence label (hours + optional minutes). */
export function formatCustomPracticeSessionDurationLabel(totalMinutes) {
  return formatSessionDurationLabel(totalMinutes);
}

/** Inline notes field appended after objective checkboxes (Progress Note). */
export const CUSTOM_PRACTICE_OBJECTIVES_NOTES_CHILD = {
  name: "note",
  key: "note",
  for: "inline-notes",
  textArea: true,
  type: "checkbox",
  placeholder: "Add your notes here",
};

export const CUSTOM_PRACTICE_CLIENT_NOTE_SECTION = {
  name: "Effectiveness / Client notes",
  key: "clientNote",
  child: [
    {
      name: "",
      key: "note",
      for: "inline-notes",
      textArea: true,
      type: "checkbox",
      placeholder: "Add your notes here",
    },
  ],
};

export function buildObjectivesChildrenFromNotes(notesArr = []) {
  const objectivesChildren = [];
  if (!notesArr || notesArr.length === 0) {
    return objectivesChildren;
  }
  for (let goalIndex = 0; goalIndex < notesArr.length; goalIndex += 1) {
    const row = notesArr[goalIndex];
    if (!row || typeof row !== 'object') {
      continue;
    }
    for (const fieldKey in row) {
      if (!fieldKey.startsWith('objective_')) {
        continue;
      }
      const uniqueKey = normalizeObjectiveNotesKey(fieldKey, goalIndex);
      const tmpTag = document.createElement('div');
      tmpTag.innerHTML = row[fieldKey];
      objectivesChildren.push({
        name: tmpTag.innerText || tmpTag.textContent,
        key: uniqueKey,
        for: 'normal',
        type: 'checkbox',
        child: [],
      });
    }
  }
  objectivesChildren.push({ ...CUSTOM_PRACTICE_OBJECTIVES_NOTES_CHILD });
  return objectivesChildren;
}

/** Progress Note: Objectives first, Client Note after Interventions */
export function buildCustomPracticeProgressSections(baseSections, notesArr = []) {
  const objectivesChildren = buildObjectivesChildrenFromNotes(notesArr);
  let sections = [...baseSections];
  if (objectivesChildren.length > 0) {
    sections.unshift({
      name: "Objectives",
      key: "objectives",
      child: objectivesChildren,
    });
  }
  const interventionsIdx = sections.findIndex((s) => s.key === "interventions");
  if (interventionsIdx !== -1) {
    sections = [
      ...sections.slice(0, interventionsIdx + 1),
      CUSTOM_PRACTICE_CLIENT_NOTE_SECTION,
      ...sections.slice(interventionsIdx + 1),
    ];
  } else {
    sections.push(CUSTOM_PRACTICE_CLIENT_NOTE_SECTION);
  }
  return sections;
}

/**
 * Assessment uses standard sections only — Objectives/Client Note are Progress Note only.
 * @deprecated Use base Assessment.json directly; kept for callers that only need notes_id fetch.
 */
export function buildCustomPracticeAssessmentSections(baseSections) {
  return [...baseSections];
}
